import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { Store } from '@ngrx/store';

import { merge, Subscription } from 'rxjs';

import { ToastrService } from 'ngx-toastr';

import { AppState } from '../../../../../../state';
import { selectSelectedLanguage, selectSupportedLanguages } from '../../../../../state/study/study.selectors';
import { Study } from '../../../../../../models/study';
import { Module } from '../../../../../../models/module';
import { Language, LanguageCode } from '../../../../../../models/language';
import { StudyType } from '../../../../../../models/study-type';
import { ModuleItem } from '../../../../../../models/module-item';
import { StudyTranslation } from '../../../../../../models/study-translation';
import { ModuleItemSubType } from '../../../../../../models/module-item-sub-type';
import { ModuleItemAvailableAttribute } from '../../../../../../models/module-item-attribute';
import {
  MODULE_ITEM_CHOICES_IDENTIFIER,
  MODULE_ITEM_DESCRIPTION_IDENTIFIER,
  MODULE_ITEM_LABEL_IDENTIFIER,
  MODULE_ITEM_QUESTION_IDENTIFIER,
  PRIVILEGE_ACTIONS,
  PRIVILEGE_SECTIONS,
  RANDOMIZATION_VALUES,
} from '../../../../../../core/dashboard-constants';
import { ModuleItemDialogComponent } from '../../module-item-dialog.component';
import { cannotContainSpace, removeWhitespaces } from '../../../../../../core/custom-form-validators';
import { PrivilegeService } from '../../../../../services/privilege/privilege.service';
import { areThereValidChoicesMessage, isThereEmptyTranslation } from '../../../../helpers/translation-helper';
import { createModuleItem, updateModuleItem } from '../../../../../state/module-item/module-item.actions';
import '../../../../../../../assets/js/tinymce-dynamic-answer.plugin';
import { ModuleItemCoreComponent } from '../module-item-core/module-item-core.component';
import { PreviewService } from '../../../../../services/preview/preview.service';
import { areThereValidNameForCalculationMessage, prepareApiVideoUrl } from '../../../../helpers/module-item-helper';
import { RoutingService } from '../../../../../services/routing/routing.service';

@Component({
  selector: 'dashboard-likert-scale',
  templateUrl: './likert-scale.component.html',
  styleUrls: ['./likert-scale.component.scss'],
})
export class LikertScaleComponent implements OnInit, OnDestroy {
  @ViewChild(ModuleItemCoreComponent, { static: true })
  dashboardModuleItemCore: ModuleItemCoreComponent;

  @Input() dialogData;

  likertScaleForm: FormGroup;

  selectedStudy: Study;
  selectedModule: Module;
  moduleItemSubType: ModuleItemSubType;
  selectedModuleItem: ModuleItem;
  isDuplicate: boolean;

  supportedLanguages: Language[];
  selectedLanguage: Language;

  isEdit: boolean;

  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;
  MODULE_ITEM_CHOICES_IDENTIFIER = MODULE_ITEM_CHOICES_IDENTIFIER;
  StudyType = StudyType;

  formSubmitted = false;

  private subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private toastrService: ToastrService,
    private moduleItemDialogRef: MatDialogRef<ModuleItemDialogComponent>,
    private previewService: PreviewService,
    private changeDetectorRef: ChangeDetectorRef,
    public privilegeService: PrivilegeService,
    private routingService: RoutingService
  ) {}

  get moduleItemCore(): AbstractControl {
    return this.likertScaleForm.get('moduleItemCore');
  }

  get translations(): FormArray {
    return this.likertScaleForm.get('translations') as FormArray;
  }

  get randomize(): AbstractControl {
    return this.likertScaleForm.get('randomize');
  }

  get allowOther(): AbstractControl {
    return this.likertScaleForm.get('allowOther');
  }

  get forCalculation(): AbstractControl {
    return this.likertScaleForm.get('forCalculation');
  }

  get calculationName(): AbstractControl {
    return this.likertScaleForm.get('calculationName');
  }

  ngOnInit(): void {
    this.likertScaleForm = this.formBuilder.group({
      moduleItemCore: [],
      translations: this.formBuilder.array([]),
      randomize: [false],
      allowOther: [false],
      forCalculation: [false],
      calculationName: ['', [Validators.maxLength(40), Validators.pattern('[a-zA-Z0-9 ]*'), cannotContainSpace]],
    });

    this.subscriptions.add(
      this.store
        .select(selectSupportedLanguages)
        .subscribe((supportedLanguages) => (this.supportedLanguages = supportedLanguages))
    );
    this.subscriptions.add(
      this.store.select(selectSelectedLanguage).subscribe((selectedLanguage) => {
        this.selectedLanguage = selectedLanguage;
        this.changeDetectorRef.detectChanges();
      })
    );

    this.selectedStudy = this.dialogData?.selectedStudy;
    this.selectedModule = this.dialogData?.selectedModule;
    this.moduleItemSubType = this.dialogData?.moduleItemSubType;
    this.selectedModuleItem = this.dialogData?.moduleItem;
    this.isDuplicate = this.dialogData?.isDuplicate;

    this.isEdit = !!this.selectedModuleItem;

    this.translations.clear();
    if (this.isEdit || this.isDuplicate) {
      if (this.supportedLanguages) {
        this.supportedLanguages.forEach((language) => this.addTranslationFields(language?.code));
      }
      if (this.selectedModuleItem?.attributes?.length) {
        const randomizeAttribute = this.selectedModuleItem.attributes.find(
          (attribute) => attribute.attribute === ModuleItemAvailableAttribute.RANDOMIZE
        );
        if (randomizeAttribute) {
          this.randomize.setValue(!!Number(randomizeAttribute.value));
        }

        const allowOtherAttribute = this.selectedModuleItem.attributes.find(
          (attribute) => attribute.attribute === ModuleItemAvailableAttribute.ALLOW_OTHER
        );
        if (allowOtherAttribute) {
          this.allowOther.setValue(!!Number(allowOtherAttribute.value));
          this.previewService.setPreviewData({
            allowOther: !!Number(allowOtherAttribute.value),
          });
        }
      }

      this.forCalculation.setValue(this.selectedModuleItem?.forCalculation);

      if (this.selectedModuleItem?.forCalculation && this.selectedModuleItem?.calculationName.length) {
        this.calculationName.setValue(this.selectedModuleItem.calculationName);
      }
    } else {
      this.selectedLanguage
        ? this.supportedLanguages.forEach((language) => this.addTranslationFields(language?.code))
        : this.addTranslationFields(this.selectedLanguage.code);
    }

    this.emitPreviewValues();

    this.subscriptions.add(
      merge(
        ...this.translations.controls.map((translationControl) => translationControl.valueChanges)
      ).subscribe((translationControlValue) =>
        this.previewService.setPreviewData(this.dashboardModuleItemCore.populatePreviewText(translationControlValue))
      )
    );

    this.subscriptions.add(
      this.randomize.valueChanges.subscribe((randomizeValueChange) =>
        this.translations.controls.forEach((translationControl) => {
          this.previewService.setPreviewData(
            this.dashboardModuleItemCore.populatePreviewText(translationControl.value, randomizeValueChange)
          );
        })
      )
    );

    this.subscriptions.add(
      this.allowOther.valueChanges.subscribe((allowOtherValueChange) =>
        this.previewService.setPreviewData({
          allowOther: !!Number(allowOtherValueChange),
        })
      )
    );

    this.subscriptions.add(
      this.forCalculation.valueChanges.subscribe((forCalculationValueChange) => {
        if (!forCalculationValueChange) {
          this.calculationName.setValue('');
        }
      })
    );
  }

  addTranslationFields(languageCode): void {
    const choicesAttribute = this.selectedModuleItem?.attributes
      ?.find((attribute) => attribute.attribute === ModuleItemAvailableAttribute.CHOICES)
      ?.translations?.find((translation) => translation?.language === languageCode);

    const choices = this.formBuilder.group({
      id: [choicesAttribute ? choicesAttribute?.id : 0],
      language: [languageCode],
      identifier: [ModuleItemAvailableAttribute.CHOICES],
      translation: [choicesAttribute ? choicesAttribute?.translation : '', [removeWhitespaces]],
    });

    this.translations.push(choices);
  }

  emitPreviewValues(): void {
    this.translations.controls.forEach((translationControl) => {
      this.previewService.setPreviewData(
        this.dashboardModuleItemCore.populatePreviewText(translationControl.value, false, this.selectedLanguage?.code)
      );
    });
  }

  buildTranslationsAndAttributesForRequest(translations: StudyTranslation[]) {
    const translationsAndAttributes = {
      translations: [],
      attributes: [],
    };

    let labelTranslation = null;

    translations?.forEach((translation) => {
      if (translation.identifier === ModuleItemAvailableAttribute.CHOICES) {
        translationsAndAttributes.attributes.push(translation);
      } else if (translation.identifier === MODULE_ITEM_LABEL_IDENTIFIER) {
        labelTranslation = translation;
        translationsAndAttributes.translations.push({
          ...translation,
          translation: this.moduleItemCore.value.label,
        });
      } else {
        translationsAndAttributes.translations.push(translation);
      }
    });

    if (!labelTranslation) {
      translationsAndAttributes.translations.push({
        id: 0,
        language: LanguageCode.ENGLISH,
        identifier: MODULE_ITEM_LABEL_IDENTIFIER,
        translation: this.moduleItemCore.value.label,
      });
    }

    const translationIdentifiersOrder = [
      MODULE_ITEM_QUESTION_IDENTIFIER,
      MODULE_ITEM_DESCRIPTION_IDENTIFIER,
      MODULE_ITEM_LABEL_IDENTIFIER,
      MODULE_ITEM_CHOICES_IDENTIFIER,
    ];
    translationsAndAttributes.translations?.sort(
      (translation1, translation2) =>
        translationIdentifiersOrder.indexOf(translation1.identifier) -
        translationIdentifiersOrder.indexOf(translation2.identifier)
    );

    return translationsAndAttributes;
  }

  saveModuleItem(): void {
    this.formSubmitted = true;

    const allTranslations = [...this.moduleItemCore.value.translations, ...this.translations.value];

    if (isThereEmptyTranslation(allTranslations, MODULE_ITEM_QUESTION_IDENTIFIER)) {
      this.toastrService.error('Please populate Question for all supported languages');
      return;
    }

    const areThereValidChoicesMsg = areThereValidChoicesMessage(
      this.supportedLanguages,
      allTranslations,
      MODULE_ITEM_CHOICES_IDENTIFIER
    );

    if (areThereValidChoicesMsg.length) {
      this.toastrService.error(areThereValidChoicesMsg);
      return;
    }

    const areThereValidNameForCalculationMsg = areThereValidNameForCalculationMessage(
      this.forCalculation.value,
      this.calculationName.value
    );

    if (areThereValidNameForCalculationMsg.length) {
      this.toastrService.error(areThereValidNameForCalculationMsg);
      return;
    }

    if (this.likertScaleForm.invalid) {
      return;
    }

    const translationsAndAttributes = this.buildTranslationsAndAttributesForRequest(allTranslations);

    let moduleItem = {
      ...this.likertScaleForm.value,
      subType: this.moduleItemSubType?.type || this.selectedModuleItem?.subType,
      type: this.moduleItemSubType?.itemType || this.selectedModuleItem?.type,
      moduleId: this.selectedModule?.id,
      isEditing: this.isEdit,
      imagePath: this.moduleItemCore.value.imagePath,
      videoUrl: this.dashboardModuleItemCore.isVideoInputVisible
        ? prepareApiVideoUrl(this.moduleItemCore.value.videoUrl)
        : '',
      translations: translationsAndAttributes.translations,
      attributes: [
        {
          attribute: ModuleItemAvailableAttribute.CHOICES,
          moduleItemId: this.selectedModuleItem?.id || 0,
          translations: translationsAndAttributes.attributes.filter(
            (attribute) => attribute.identifier === ModuleItemAvailableAttribute.CHOICES
          ),
        },
        {
          attribute: ModuleItemAvailableAttribute.RANDOMIZE,
          moduleItemId: this.selectedModuleItem?.id || 0,
          value: +this.randomize.value,
        },
        {
          attribute: ModuleItemAvailableAttribute.ALLOW_OTHER,
          moduleItemId: this.selectedModuleItem?.id || 0,
          value: +this.allowOther.value,
        },
      ],
      routing: this.routingService.isRoutingDataChanged()
        ? this.routingService.getRoutingData()
        : this.selectedModuleItem?.routing,
    };

    if (this.selectedModule?.randomization === RANDOMIZATION_VALUES.CATEGORY) {
      if (!this.moduleItemCore.value.categoryId) {
        this.toastrService.error('Please select Category Label');
        return;
      }

      moduleItem = {
        ...moduleItem,
        categoryId: this.moduleItemCore.value.categoryId,
      };
    }

    delete moduleItem.moduleItemCore;
    delete moduleItem.label;
    delete moduleItem.randomize;
    delete moduleItem.allowOther;

    if (moduleItem.routing && moduleItem.routing.routingGroups && !moduleItem.routing.routingGroups.length) {
      delete moduleItem.routing;
    }

    if (this.routingService.isRoutingDataChanged()) {
      this.routingService.resetIsRoutingDataChanged();
    }

    if (!moduleItem.forCalculation && this.selectedModuleItem) {
      moduleItem.calculationName = this.selectedModuleItem.calculationName;
    }

    if (this.isEdit && !this.isDuplicate) {
      moduleItem = {
        ...moduleItem,
        id: this.selectedModuleItem?.id,
      };
      this.store.dispatch(
        updateModuleItem({
          studyId: this.selectedStudy?.id,
          moduleId: this.selectedModule?.id,
          moduleItem,
        })
      );
    } else {
      this.store.dispatch(
        createModuleItem({
          studyId: this.selectedStudy?.id,
          moduleId: this.selectedModule?.id,
          moduleItem,
        })
      );
    }
  }

  closeModuleItemDialog(): void {
    this.moduleItemDialogRef.close();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
